import axios from "axios";
import API_URL from "./Config";
import refreshToken from "./Refresh"; // must return the NEW access token

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authKey");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) return Promise.reject(error);

    const original = error.config;
    const status = error.response.status;

    // Skip token refresh for logout endpoint - just reject immediately
    const isLogoutRequest = original.url?.includes('/logout') || 
                           original.url?.includes('/api/logout');
    const isPasswordRecoveryRequest =
      original.url?.includes("/api/forget_password") ||
      original.url?.includes("/api/verify_otp") ||
      original.url?.includes("/api/reset_password");
    
    if (isLogoutRequest || isPasswordRecoveryRequest) {
      return Promise.reject(error);
    }

    // Handle both 401 and 403
    if ((status === 401 || status === 403) && !original._retry) {
      original._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers["Authorization"] = "Bearer " + token;
          return axiosInstance(original);
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshToken();

        if (!newToken) {
          processQueue(error, null);
          // Don't clear storage or redirect - just reject
          return Promise.reject(error);
        }

        localStorage.setItem("authKey", newToken);
        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${newToken}`;

        processQueue(null, newToken);

        original.headers["Authorization"] = "Bearer " + newToken;
        return axiosInstance(original);
      } catch (err) {
        processQueue(err, null);
        // Don't clear storage or redirect - just reject
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
